// Slot booking types for India Blood Vision checkout

export interface TimeSlot {
  id: string;
  startTime: string; // Format: "HH:MM" (24-hour)
  endTime: string; // Format: "HH:MM" (24-hour)
  displayTime: string; // Format: "9:00 AM - 10:00 AM"
  available: boolean;
  charge?: number; // Additional charge for the slot
  metaText?: string; // Display text like "+ Rs.79 Only"
  labId?: number; // Lab ID
  epochSlot?: number; // Unix timestamp
}

// API Response Types
export interface ApiTimeSlot {
  title: string; // e.g., "7:00 AM"
  id: string; // Base64 encoded slot ID
  epoch_slot: number;
  timestamp: string;
  available: boolean;
  charge: number;
  meta_text: string; // e.g., "+ Rs.79 Only"
  lab_id: number;
}

export interface ApiDateSlot {
  title: string; // Day number like "06"
  subtitle: string; // Month like "Nov"
  time_slots_data: {
    title: string; // "TIME"
    slots: ApiTimeSlot[];
  };
}

export interface ApiSlotsResponse {
  data: {
    page_title: string;
    page_subtitle: string;
    button_data: {
      title: string;
      deeplink: string | null;
    };
    dates_data: {
      title: string;
      dates: ApiDateSlot[];
    };
  };
  error: string | null;
  status: 'ok' | 'error';
}

export interface DateSlot {
  date: string; // ISO format: "YYYY-MM-DD"
  dayName: string; // "Today", "Tomorrow", or day name like "Monday" or "MON"
  displayDate: string; // Format: "15 Nov"
  monthName?: string; // Short month name like "NOV" (for circular cards)
  dayNumber?: string; // Day number like "15" (for circular cards)
  slots: TimeSlot[];
  available: boolean; // Whether any slots are available for this date
}

export interface SlotBooking {
  selectedDate: string | null; // ISO format
  selectedSlot: TimeSlot | null;
}

// Mock data generator for testing
// daysBackward: how many days to go back from today (default 5)
// daysForward: how many days to go forward from today (default 10)
// showTodayTomorrow: whether to show "Today"/"Tomorrow" labels (default true)
export const generateMockSlotData = (
  daysBackward = 5,
  daysForward = 10,
  showTodayTomorrow = true,
): DateSlot[] => {
  const slots: DateSlot[] = [];
  const today = new Date();

  // Common time slots
  const timeSlotTemplates = [
    { start: '06:00', end: '07:00', display: '6:00 AM - 7:00 AM' },
    { start: '07:00', end: '08:00', display: '7:00 AM - 8:00 AM' },
    { start: '08:00', end: '09:00', display: '8:00 AM - 9:00 AM' },
    { start: '09:00', end: '10:00', display: '9:00 AM - 10:00 AM' },
    { start: '10:00', end: '11:00', display: '10:00 AM - 11:00 AM' },
    { start: '11:00', end: '12:00', display: '11:00 AM - 12:00 PM' },
  ];

  // Generate dates from (today - daysBackward) to (today + daysForward)
  const totalDays = daysBackward + daysForward + 1;
  const startOffset = -daysBackward;

  for (let i = 0; i < totalDays; i++) {
    const dayOffset = startOffset + i;
    const date = new Date(today);
    date.setDate(today.getDate() + dayOffset);

    const isoDate = date.toISOString().split('T')[0];
    const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    let dayName: string;
    if (showTodayTomorrow) {
      if (dayOffset === 0) dayName = 'Today';
      else if (dayOffset === 1) dayName = 'Tomorrow';
      else if (dayOffset === -1) dayName = 'Yesterday';
      else dayName = dayNamesShort[date.getDay()];
    } else {
      dayName = dayNamesShort[date.getDay()];
    }

    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const displayDate = `${date.getDate()} ${monthNames[date.getMonth()]}`;

    // Generate time slots with random availability
    const timeSlots: TimeSlot[] = timeSlotTemplates.map((template, idx) => ({
      id: `${isoDate}-slot-${idx}`,
      startTime: template.start,
      endTime: template.end,
      displayTime: template.display,
      available: Math.random() > 0.3, // 70% chance of being available
    }));

    // Make some specific dates completely unavailable for demo purposes
    // Make every 4th date unavailable (e.g., indices 3, 7, 11, 15...)
    const isDateUnavailable = i % 4 === 3;
    const hasAvailableSlots = isDateUnavailable
      ? false
      : timeSlots.some((slot) => slot.available);

    const dayNumberStr = date.getDate().toString();
    slots.push({
      date: isoDate,
      dayName,
      displayDate,
      monthName: monthNames[date.getMonth()],
      dayNumber: dayNumberStr,
      slots: timeSlots,
      available: hasAvailableSlots,
    });
  }

  return slots;
};

// API Service Function
export const fetchSlotsAvailability = async (params: {
  email: string;
  datetime: number; // Unix timestamp
  packageIds: number[]; // Array of package IDs
  pincode: string;
}): Promise<ApiSlotsResponse> => {
  const { email, datetime, packageIds, pincode } = params;

  // Build query string
  const queryParams = new URLSearchParams({
    email: email,
    datetime: datetime.toString(),
    package_ids: packageIds.join(','),
    pincode: pincode,
  });

  const url = `/api/blood-vision/india/slots_availability?${queryParams}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data: ApiSlotsResponse = await response.json();

  if (data.status !== 'ok') {
    throw new Error(data.error || 'Failed to fetch slots');
  }

  return data;
};

// Transform API response to DateSlot format
export const transformApiSlotsToDateSlots = (
  apiResponse: ApiSlotsResponse,
): DateSlot[] => {
  if (
    !apiResponse?.data?.dates_data?.dates ||
    !Array.isArray(apiResponse.data.dates_data.dates)
  ) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return apiResponse.data.dates_data.dates
    .filter((apiDate) => {
      // Filter out invalid entries
      return (
        apiDate &&
        apiDate.title &&
        apiDate.subtitle &&
        apiDate.time_slots_data?.slots
      );
    })
    .map((apiDate) => {
      try {
        // Parse the date from title (day) and subtitle (month)
        const dayNumberParsed = parseInt(
          String(apiDate.title || '').trim(),
          10,
        );
        const monthName = String(apiDate.subtitle || '').trim();

        // Validate parsed day number
        if (
          isNaN(dayNumberParsed) ||
          dayNumberParsed < 1 ||
          dayNumberParsed > 31
        ) {
          console.warn(`Invalid day number: ${apiDate.title}, using fallback`);
          return null;
        }

        // Validate month name
        const monthIndex = monthNames.findIndex(
          (m) => m.toLowerCase() === monthName.toLowerCase(),
        );
        if (monthIndex === -1) {
          console.warn(`Invalid month name: ${monthName}, skipping date`);
          return null;
        }

        // Get current year (handle year transition)
        const currentYear = today.getFullYear();

        // Create date object at noon to avoid timezone issues
        const slotDate = new Date(
          currentYear,
          monthIndex,
          dayNumberParsed,
          12,
          0,
          0,
        );

        // Validate the date object
        if (isNaN(slotDate.getTime())) {
          console.warn(
            `Invalid date created from ${dayNumberParsed} ${monthName}, skipping`,
          );
          return null;
        }

        // If the date is in the past and more than 6 months ago, assume it's next year
        if (
          slotDate < today &&
          today.getTime() - slotDate.getTime() > 180 * 24 * 60 * 60 * 1000
        ) {
          slotDate.setFullYear(currentYear + 1);
        }

        // Format to ISO date string manually to avoid timezone issues
        const isoDate = `${slotDate.getFullYear()}-${String(
          slotDate.getMonth() + 1,
        ).padStart(2, '0')}-${String(slotDate.getDate()).padStart(2, '0')}`;
        const dayName = dayNamesShort[slotDate.getDay()] || 'Sun';
        const displayDate = `${slotDate.getDate()} ${monthName}`;

        // Use the actual date object's day number (always valid)
        const dayNumber = slotDate.getDate().toString();

        // Transform time slots with validation
        const timeSlots: TimeSlot[] = (apiDate.time_slots_data?.slots || [])
          .filter((apiSlot) => apiSlot && apiSlot.id) // Filter out invalid slots
          .map((apiSlot) => {
            try {
              // Parse the time from title
              // Handle formats: "4:00 - 5:00 PM", "11:00 AM - 12:00 PM", or "7:00 AM"
              const timeRangeMatch = apiSlot.title.match(
                /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
              );
              const timeRangeMixedMatch = apiSlot.title.match(
                /^(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
              );
              const singleTimeMatch = apiSlot.title.match(
                /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
              );

              let startTime = '00:00';
              let displayTime = String(apiSlot.title || '').trim() || '';

              if (timeRangeMixedMatch) {
                // Handle mixed format: "11:00 AM - 12:00 PM"
                let hours = parseInt(timeRangeMixedMatch[1], 10);
                const minutes = timeRangeMixedMatch[2];
                const startMeridiem = timeRangeMixedMatch[3].toUpperCase();

                // Convert to 24-hour format
                if (startMeridiem === 'PM' && hours !== 12) {
                  hours += 12;
                } else if (startMeridiem === 'AM' && hours === 12) {
                  hours = 0;
                }

                startTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
                displayTime = apiSlot.title;
              } else if (timeRangeMatch) {
                // Handle range format: "4:00 - 5:00 PM" (both times share same AM/PM)
                let hours = parseInt(timeRangeMatch[1], 10);
                const minutes = timeRangeMatch[2];
                const meridiem = timeRangeMatch[5].toUpperCase();

                // Convert to 24-hour format
                if (meridiem === 'PM' && hours !== 12) {
                  hours += 12;
                } else if (meridiem === 'AM' && hours === 12) {
                  hours = 0;
                }

                startTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
                displayTime = apiSlot.title;
              } else if (singleTimeMatch) {
                // Handle single time format: "7:00 AM"
                let hours = parseInt(singleTimeMatch[1], 10);
                const minutes = singleTimeMatch[2];
                const meridiem = singleTimeMatch[3].toUpperCase();

                // Convert to 24-hour format
                if (meridiem === 'PM' && hours !== 12) {
                  hours += 12;
                } else if (meridiem === 'AM' && hours === 12) {
                  hours = 0;
                }

                startTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
                displayTime = apiSlot.title;
              } else {
                // If no match, log warning but still create slot with default values
                console.warn(`Could not parse time format: ${apiSlot.title}`);
              }

              // Parse epoch slot - prefer epoch_slot (number), fallback to timestamp (string)
              let epochSlot: number | undefined;
              if (
                typeof apiSlot.epoch_slot === 'number' &&
                !isNaN(apiSlot.epoch_slot)
              ) {
                epochSlot = apiSlot.epoch_slot;
              } else if (apiSlot.timestamp) {
                // Fallback to timestamp if epoch_slot is not available
                const parsedTimestamp = parseInt(String(apiSlot.timestamp), 10);
                if (!isNaN(parsedTimestamp)) {
                  epochSlot = parsedTimestamp;
                }
              }

              return {
                id: String(apiSlot.id || ''),
                startTime: startTime,
                endTime: startTime, // API doesn't provide separate end time
                displayTime: displayTime,
                available: Boolean(apiSlot.available),
                charge:
                  typeof apiSlot.charge === 'number'
                    ? apiSlot.charge
                    : undefined,
                metaText: String(apiSlot.meta_text || ''),
                labId:
                  typeof apiSlot.lab_id === 'number'
                    ? apiSlot.lab_id
                    : undefined,
                epochSlot: epochSlot,
              } as TimeSlot;
            } catch (error) {
              console.warn('Error parsing time slot:', error, apiSlot);
              return null;
            }
          })
          .filter((slot): slot is TimeSlot => slot !== null);

        // Check if any slots are available
        const hasAvailableSlots =
          timeSlots.length > 0 && timeSlots.some((slot) => slot.available);

        const dateSlot: DateSlot = {
          date: isoDate,
          dayName,
          displayDate,
          monthName: monthName,
          dayNumber: dayNumber, // Use validated day number from date object
          slots: timeSlots,
          available: hasAvailableSlots,
        };

        return dateSlot;
      } catch (error) {
        console.error('Error transforming date slot:', error, apiDate);
        return null;
      }
    })
    .filter((slot): slot is DateSlot => slot !== null);
};
